import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import ModalConfirm from "@/components/modal-confirm"
import Modal from "@/components/modal"
import TextInput from "@/components/text-input"
import { Role } from "@/redux/role.types"
import { STATUS_SAVING } from "@/redux/_status.types"

interface Props {
  role?: Role
  accountId: string
  onClose: () => void
  onSave: (role: Role) => void | Promise<void>
  onDelete?: (role: Role) => void | Promise<void>
}

export default function RoleEditModal({ role, accountId, onClose, onSave, onDelete }: Props) {
  const { t } = useTranslation()
  const [name, setName] = useState(role?.name ?? "")

  const [isSaving, setIsSaving] = useState(false)
  const savingRef = useRef(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  async function save() {
    const trimmedName = name.trim()
    if (savingRef.current || !trimmedName || trimmedName.length > 255) return
    savingRef.current = true
    setIsSaving(true)
    try {
      await onSave(
        new Role({
          ...role,
          id: role?.id ?? crypto.randomUUID(),
          AccountId: role?.AccountId ?? accountId,
          name: trimmedName
        })
      )
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  async function deleteRole() {
    if (!role || !onDelete || savingRef.current) return
    savingRef.current = true
    setIsSaving(true)
    try {
      await onDelete(role)
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  if (confirmDelete) {
    return (
      <ModalConfirm
        textBold={t("roles_confirm_delete", { name: role?.name })}
        onNo={() => {
          if (!savingRef.current) setConfirmDelete(false)
        }}
        onYes={deleteRole}
        status={isSaving ? STATUS_SAVING : undefined}
      />
    )
  }

  return (
    <Modal
      title={t(role ? "roles_edit_role" : "roles_add_role")}
      onClose={() => {
        if (!savingRef.current) onClose()
      }}
      status={isSaving ? STATUS_SAVING : undefined}
      onNext={save}
      isCloseButtonVisible
      onDelete={
        role && onDelete
          ? () => {
              if (!savingRef.current) setConfirmDelete(true)
            }
          : undefined
      }
      onNextCta={t("utils_save")}
      nextDisabled={isSaving || !name.trim() || name.trim().length > 255}>
      <TextInput
        id="roleName"
        title={t("roles_name")}
        value={name}
        disabled={isSaving}
        error={name.trim().length > 255}
        help={name.trim().length > 255 ? t("roles_invalid_name") : undefined}
        onChange={(event: { value: string }) => setName(event.value)}
        autoFocus
        isFullWidth
        required
      />
      <div className="height-20" />
    </Modal>
  )
}
